import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import * as log from '../../util/log';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { convertMoneyToNumber, formatMoney } from '../../util/currency';
import { getStartOf, timestampToDate } from '../../util/dates';
import { hasPermissionToInitiateTransactions, isUserAuthorized } from '../../util/userHelpers';
import { requiresSoftReservationFallback, usesStockTrack } from '../../util/softReservation';
import {
  clearPendingBooking,
  readPendingBooking,
  savePendingBooking,
  saveSoftBookingData,
} from '../../util/pendingBooking';
import { buildGa4Item, track } from '../../util/analytics';
import { adClickIds } from '../../util/attribution';
import {
  NO_ACCESS_PAGE_INITIATE_TRANSACTIONS,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  createSlug,
  pathWithLoginModal,
} from '../../util/urlHelpers';
import { REQUEST } from '../../transactions/transaction';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_MONTH,
  LINE_ITEM_NIGHT,
  LINE_ITEM_WEEK,
  LISTING_STATE_CLOSED,
} from '../../util/types';

import { Page, LayoutSingleColumn } from '../../components';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './ListingPage.module.css';

/**
 * This file contains shared functions from each ListingPage variants.
 */

const { UUID } = sdkTypes;

/**
 * Meldet einen Inseratsaufruf (`view_item`) — genau einmal pro Inserat.
 *
 * **Warum das hier steht und nicht mehr im Duck:** Vorher feuerte das Ereignis in
 * `loadData` (ListingPage.duck.js). Beim Direkteinstieg läuft `loadData` aber im
 * SSR, und dort gibt es kein `window` — `send()` in util/analytics.js steigt dann
 * sofort aus. Nach der Hydration wird `loadData` für die Einstiegsroute nicht noch
 * einmal ausgeführt, der Aufruf war also für immer verloren. Gezählt wurden damit
 * ausschliesslich In-App-Navigationen.
 *
 * Das traf ausgerechnet bezahlten Traffic am härtesten: Ein Anzeigenklick landet
 * per Definition direkt auf dem Inserat und erzeugte deshalb weder `view_item`
 * (GA4) noch `ViewContent` (Reddit) — kein Mid-Funnel-Signal, keine wachsende
 * Retargeting-Zielgruppe, zu niedrige Funnel-Zahlen. Auf Prod am 26.08.2026
 * nachgemessen: Direktaufruf → nur `PageVisit`, `ViewContent` erst nach einem
 * Klick innerhalb der App.
 *
 * Im Effekt der Komponente feuert es in beiden Fällen genau einmal.
 *
 * @param {Object} listing Das aktuelle Inserat (`ensureListing`-Ergebnis)
 * @param {boolean} isOwnVariant Eigene Vorschau (Entwurf/Freigabe/nur eigene
 *   Inserate) — dieselbe Bedingung, unter der auch der Duck nichts meldete
 * @returns {void}
 */
export const useTrackListingView = (listing, isOwnVariant) => {
  const trackedListingId = useRef(null);

  const listingId = listing?.id?.uuid;
  // `ensureListing` liefert immer ein Objekt, im Zweifel eine leere Hülle. Erst
  // ein Titel belegt, dass die Daten wirklich da sind — sonst meldeten wir den
  // Platzhalter des ersten Renders.
  const isLoaded = !!listingId && !!listing?.attributes?.title;

  useEffect(() => {
    if (isOwnVariant || !isLoaded || trackedListingId.current === listingId) {
      return;
    }
    trackedListingId.current = listingId;

    const price = listing?.attributes?.price;
    const publicData = listing?.attributes?.publicData || {};
    track.viewItem({
      listing_id: listingId,
      item_name: listing?.attributes?.title,
      type: publicData.listingType,
      city: publicData.city || publicData.location?.address,
      value: price ? price.amount / 100 : undefined,
      currency: price?.currency,
      items: [buildGa4Item(listing)],
      ...adClickIds(),
    });
  }, [listing, listingId, isLoaded, isOwnVariant]);
};

/**
 * Helper to get formattedPrice and priceTitle for SectionHeading component.
 * @param {Money} price listing's price
 * @param {String} marketplaceCurrency currency of the price (e.g. 'USD')
 * @param {Object} intl React Intl instance
 * @returns Object literal containing formattedPrice and priceTitle
 */
export const priceData = (price, marketplaceCurrency, intl) => {
  if (price && price.currency === marketplaceCurrency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

/**
 * Converts Money object to number, which is needed for the search schema (for Google etc.)
 *
 * @param {Money} price
 * @returns {Money|null}
 */
export const priceForSchemaMaybe = price => {
  try {
    const schemaPrice = convertMoneyToNumber(price);
    return {
      price: schemaPrice.toFixed(2),
      priceCurrency: price.currency,
    };
  } catch (e) {
    return {};
  }
};

/**
 * Get category's label.
 *
 * @param {Array} categories array of category objects (key & label)
 * @param {String} value selected category value
 * @returns label for the selected value
 */
export const categoryLabel = (categories, value) => {
  const cat = categories.find(c => c.key === value);
  return cat ? cat.label : value;
};

/**
 * Filter listing images with correct custom image variant name.
 * Used for facebook, twitter and page schema images.
 *
 * @param {Listing} listing
 * @param {String} variantName
 * @returns correct image variant specified by variantName parameter.
 */
export const listingImages = (listing, variantName) =>
  (listing.images || [])
    .map(image => {
      const variants = image.attributes.variants;
      const variant = variants ? variants[variantName] : null;

      // deprecated
      // for backwards combatility only
      const sizes = image.attributes.sizes;
      const size = sizes ? sizes.find(i => i.name === variantName) : null;

      return variant || size;
    })
    .filter(variant => variant != null);

/**
 * Callback for the "contact" button on ListingPage to open inquiry modal.
 *
 * @param {Object} parameters all the info needed to open inquiry modal.
 */
export const handleContactUser = parameters => () => {
  const {
    history,
    params,
    currentUser,
    isAuthenticated,
    callSetInitialValues,
    location,
    routes,
    setInitialValues,
    setInquiryModalOpen,
  } = parameters;

  if (!currentUser) {
    // Contacting the host supersedes a booking the visitor started and walked away from: without
    // this, signing in from the inquiry dialog would let usePendingBookingResume fire first and
    // push them into checkout instead of opening the inquiry they just asked for.
    //
    // Gated on isAuthenticated, not on the `!currentUser` above: fetchCurrentUser is dispatched
    // only after hydrateRoot, so a visitor returning from a social sign-in is authenticated for a
    // few hundred milliseconds before their user object lands. Clearing on `!currentUser` alone
    // destroyed the booking intent the entire sign-in detour was for if they tapped "contact host"
    // in that window.
    if (isAuthenticated !== true) {
      clearPendingBooking();
    }

    // Remember what the visitor was trying to do. The ListingPage components watch this and open
    // the inquiry once the visitor is actually signed in — the dialog resolves in place, so the
    // page never remounts and cannot pick the flag up from its initial state the way the old
    // redirect-to-/signup flow did.
    callSetInitialValues(setInitialValues, { inquiryModalOpenForListingId: params.id });

    // Open the login dialog on top of the listing instead of navigating to /signup. Sending
    // someone away from the listing at the exact moment they decided to contact the host is the
    // most expensive place in the app to lose context, and it is unnecessary: the dialog is
    // already mounted in the Topbar on every page, and its open state is just this URL param.
    // `replace`, not `push` — opening a dialog is not a place in the history.
    history.replace(pathWithLoginModal(location));
  } else if (!isUserAuthorized(currentUser)) {
    // A user in pending-approval state can't contact the author (the same applies for a banned user)
    const pathParams = { missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL };
    history.push(createResourceLocatorString('NoAccessPage', routes, pathParams, {}));
  } else if (!hasPermissionToInitiateTransactions(currentUser)) {
    // A user in pending-approval state can't contact the author (the same applies for a banned user)
    const pathParams = { missingAccessRight: NO_ACCESS_PAGE_INITIATE_TRANSACTIONS };
    history.push(createResourceLocatorString('NoAccessPage', routes, pathParams, {}));
  } else {
    setInquiryModalOpen(true);
  }
};

/**
 * Callback for the inquiry modal to submit aka create inquiry transaction on ListingPage.
 * Note: this is for booking and purchase processes. Inquiry process is handled through handleSubmit.
 *
 * @param {Object} parameters all the info needed to create inquiry.
 */
export const handleSubmitInquiry = parameters => values => {
  const { history, params, getListing, onSendInquiry, routes, setInquiryModalOpen } = parameters;

  const listingId = new UUID(params.id);
  const listing = getListing(listingId);
  const { message } = values;

  onSendInquiry(listing, message.trim())
    .then(txId => {
      setInquiryModalOpen(false);

      // B2C demand-side lead. Fired as `contact_host`, NOT `generate_lead`, so it maps to its
      // own Ads conversion action and can never pollute the supply-side space-owner campaign.
      const price = listing?.attributes?.price;
      track.contactHost({
        lead_type: 'inquiry',
        listing_id: listing?.id?.uuid,
        value: price ? price.amount / 100 : undefined,
        currency: price?.currency,
      });

      const transactionPage =
        listing.attributes.publicData.unitType === REQUEST ? 'SaleDetailsPage' : 'OrderDetailsPage';
      // Redirect to OrderDetailsPage or SaleDetailsPage
      history.push(createResourceLocatorString(transactionPage, routes, { id: txId.uuid }, {}));
    })
    .catch(() => {
      // Ignore, error handling in duck file
    });
};

/**
 * Handle navigation to MakeOfferPage. Returns a function that can be used as a form submit handler.
 * Note: this does not yet handle form values, it only navigates to the MakeOfferPage.
 *
 * @param {Object} parameters all the info needed to navigate to MakeOfferPage.
 * @param {Object} parameters.getListing The getListing function from react-router.
 * @param {Object} parameters.params The params object from react-router.
 * @param {Object} parameters.history The history object from react-router.
 * @param {Object} parameters.routes The routes object from react-router.
 * @returns {Function} A function that navigates to MakeOfferPage.
 */
export const handleNavigateToMakeOfferPage = parameters => () => {
  const { getListing, params, history, routes } = parameters;

  const listingId = new UUID(params.id);
  const listing = getListing(listingId);

  history.push(
    createResourceLocatorString(
      'MakeOfferPage',
      routes,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    )
  );
};

/**
 * Handle navigation to RequestQuotePage. Returns a function that can be used as a form submit handler.
 * Note: this does not yet handle form values, it only navigates to the RequestQuotePage.
 *
 * @param {Object} parameters all the info needed to navigate to RequestQuotePage.
 * @param {Object} parameters.getListing The getListing function from react-router.
 * @param {Object} parameters.params The params object from react-router.
 * @param {Object} parameters.history The history object from react-router.
 * @param {Object} parameters.routes The routes object from react-router.
 * @returns {Function} A function that navigates to RequestQuotePage.
 */
export const handleNavigateToRequestQuotePage = parameters => () => {
  const { getListing, params, history, routes } = parameters;

  const listingId = new UUID(params.id);
  const listing = getListing(listingId);

  history.push(
    createResourceLocatorString(
      'RequestQuotePage',
      routes,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    )
  );
};

const dateFromFieldValue = fieldValue => {
  if (fieldValue instanceof Date) {
    return fieldValue;
  }
  if (fieldValue?.date instanceof Date) {
    return fieldValue.date;
  }
  return null;
};

/**
 * The booking window the order panel's form values describe, whichever of the three date fields
 * the listing's unit type happens to use.
 *
 * Extracted from handleSubmit so bookingSummaryDates can show the visitor the dates they picked
 * without re-deriving them from a different shape and drifting out of sync with what is actually
 * submitted.
 *
 * @param {Object} values order panel form values
 * @returns {{bookingStart: Date, bookingEnd: Date}|null}
 */
const bookingDatesFromValues = values => {
  const { bookingDates, bookingStartTime, bookingEndTime, bookingStartDate, bookingEndDate } =
    values || {};

  if (bookingDates?.startDate && bookingDates?.endDate) {
    return { bookingStart: bookingDates.startDate, bookingEnd: bookingDates.endDate };
  }
  if (bookingStartTime && bookingEndTime) {
    return {
      bookingStart: timestampToDate(bookingStartTime),
      bookingEnd: timestampToDate(bookingEndTime),
    };
  }
  const bookingStartDateValue = dateFromFieldValue(bookingStartDate);
  const bookingEndDateValue = dateFromFieldValue(bookingEndDate);
  if (bookingStartDateValue && bookingEndDateValue) {
    return { bookingStart: bookingStartDateValue, bookingEnd: bookingEndDateValue };
  }
  return null;
};

// The API's end date is exclusive for these three — BookingDatesForm's own isDayBasedUnit uses the
// same list — so the picker's inclusive end has to be reconstructed for display.
const EXCLUSIVE_END_UNITS = [LINE_ITEM_DAY, LINE_ITEM_WEEK, LINE_ITEM_MONTH];
// Rendered as plain dates, no time of day. Nightly stays belong here too even though their end is
// NOT exclusive: a hotel-style 14–16 Sep has a real checkout date, and printing it as
// "14. Sep, 00:00 – 16. Sep, 00:00" is two meaningless midnights.
const DATE_ONLY_UNITS = [...EXCLUSIVE_END_UNITS, LINE_ITEM_NIGHT];

/**
 * The booking window as the visitor saw it, for showing back to them in the login dialog.
 *
 * Two corrections the raw submit values need before they can be displayed:
 *   - Day-based bookings carry the API's *exclusive* end date, one day past what the picker showed
 *     (BookingDatesForm applies getExclusiveEndDate on the way in and reverses it for display).
 *     Printing it verbatim would tell someone who booked a single day pass that they picked two.
 *   - The instants are anchored to the listing's zone, not the browser's. A Zurich visitor booking
 *     an Istanbul lounge picked 09:00 there, and 08:00 is not a reassuring thing to show them.
 *
 * @param {{bookingStart: Date, bookingEnd: Date}|null} bookingDates
 * @param {Object} listing
 */
const bookingSummaryDates = (bookingDates, listing) => {
  if (!bookingDates) {
    return {};
  }
  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const lineItemUnit = `line-item/${listing?.attributes?.publicData?.unitType}`;
  const hasExclusiveEnd = EXCLUSIVE_END_UNITS.includes(lineItemUnit);
  const isDateOnly = DATE_ONLY_UNITS.includes(lineItemUnit);
  const { bookingStart, bookingEnd } = bookingDates;

  return {
    bookingStart,
    bookingEnd: hasExclusiveEnd ? getStartOf(bookingEnd, 'day', timeZone, -1, 'days') : bookingEnd,
    isDateOnly,
    timeZone,
  };
};

/**
 * Handle order submit from OrderPanel.
 *
 * A logged-out visitor is not sent to /login or /signup from here. They have just done the most
 * expensive work the funnel asks for — picking dates, a time and a price variant — and a
 * navigation would throw all of it away: the auth guard in Routes.js redirects with nothing but a
 * `from` path, so the standard checkout came back empty and the soft-booking page, which reads its
 * payload out of history state, came back broken. Instead the selection is stored and the login
 * dialog opens on top of the listing; usePendingBookingResume replays this exact submit once they
 * are signed in.
 *
 * @param {Object} parameters all the info needed to redirect user to CheckoutPage.
 * @param {boolean} [parameters.isAuthenticated] - when false, the visitor is asked to sign in
 *   first. Falls back to the presence of currentUser when not given.
 * @param {Object} [parameters.location] - needed to open the login dialog in place
 */
export const handleSubmit = parameters => values => {
  const {
    history,
    params,
    currentUser,
    isAuthenticated,
    location,
    getListing,
    callSetInitialValues,
    onInitializeCardPaymentData,
    onClearInquiryIntent,
    routes,
  } = parameters;
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);

  const {
    priceVariantName, // relevant for bookings
    quantity: quantityRaw,
    seats: seatsRaw,
    deliveryMethod,
    bookingDates, // consumed by bookingDatesFromValues (omit)
    bookingStartTime, // consumed by bookingDatesFromValues (omit)
    bookingEndTime, // consumed by bookingDatesFromValues (omit)
    bookingStartDate, // not relevant (omit)
    bookingEndDate, // not relevant (omit)
    ...otherOrderData
  } = values;

  const bookingDatesMaybe = bookingDatesFromValues(values);
  const seats = Number.parseInt(seatsRaw, 10);

  // Signed out: keep the selection, ask for the account, come back to this same call.
  //
  // Only take this path if the selection was actually stored. On a browser that refuses storage
  // there is nothing to come back to, and silently opening the dialog would strand the visitor on
  // a listing with an empty panel. Falling through instead runs the plain navigation, which the
  // auth guard carries `location.state` through (Routes.js) — worse than the dialog, but it works.
  const isLoggedOut = isAuthenticated != null ? !isAuthenticated : !currentUser;
  // A soft booking needs SOMETHING to replay. On the booking track that is a window — the server
  // does `new Date(bookingStart)` and initiates with it, so a dateless payload cannot become a
  // transaction. On the stock track (purchase process, e.g. fixdesk) it is the quantity, and
  // ProductOrderForm always supplies one. Until that track existed this whole combination was
  // unreplayable and the interception was skipped; now only a booking without dates is.
  // (The panel no longer promises a kept selection in words — its submit label only says that a
  // sign-in comes first, which stays true on the fallback path below too.)
  const isResumable = !(
    requiresSoftReservationFallback(listing) &&
    !bookingDatesMaybe &&
    !usesStockTrack(listing)
  );
  const isSelectionKept =
    isLoggedOut &&
    isResumable &&
    !!location &&
    savePendingBooking({
      listingId: params.id,
      values,
      listingTitle: listing?.attributes?.title,
      ...bookingSummaryDates(bookingDatesMaybe, listing),
      seats: Number.isInteger(seats) ? seats : undefined,
    });

  // The mirror of handleContactUser dropping the pending booking: whichever of the two the visitor
  // chose last is the one they meant. Left armed, the inquiry marker reopens the "message the host"
  // dialog on this listing on every later visit — ListingPage.duck's loadData deliberately re-seeds
  // it, and only sendInquiry clears it. Outside the signed-out branch on purpose: that re-seeding
  // only happens for an authorized user, so hanging the reset on the logged-out path would have
  // been the one population it can never help.
  onClearInquiryIntent?.();

  if (isSelectionKept) {
    // How many logged-out visitors reach the account wall is the number this whole flow is judged
    // on. cta_click carries it without minting a new semantic event (and stays out of Reddit,
    // where a mid-funnel step would only dilute the Purchase signal).
    track.ctaClick({
      click_id: 'booking_login_required',
      click_text: 'booking_cta',
      listing_id: params.id,
    });

    history.replace(pathWithLoginModal(location));
    return;
  }

  const bookingMaybe = bookingDatesMaybe ? { bookingDates: bookingDatesMaybe } : {};
  // priceVariantName is relevant for bookings
  const priceVariantNameMaybe = priceVariantName ? { priceVariantName } : {};
  const quantity = Number.parseInt(quantityRaw, 10);
  const quantityMaybe = Number.isInteger(quantity) ? { quantity } : {};
  const seatsMaybe = Number.isInteger(seats) ? { seats } : {};
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};

  const initialValues = {
    listing,
    orderData: {
      ...bookingMaybe,
      ...priceVariantNameMaybe,
      ...quantityMaybe,
      ...seatsMaybe,
      ...deliveryMethodMaybe,
      ...otherOrderData,
    },
    confirmPaymentError: null,
  };

  const saveToSessionStorage = !currentUser;

  // Route to soft-booking checkout when the provider has not yet connected Stripe.
  // requiresSoftReservationFallback() returns true when:
  //   1. The listing uses a payment process (booking / purchase / negotiation-offer), AND
  //   2. The provider's profile.publicData.stripeConnected is falsy.
  // We intentionally omit the hasPermissionToInitiateTransactions() check here:
  // that function requires effectivePermissionSet to be included in the currentUser
  // fetch; when it is absent the function always returns false, which would silently
  // block the soft-booking route even for fully authorized users.
  // The /soft-booking-checkout route is auth-guarded and the server validates
  // permissions at initiation time, so skipping it here is safe.
  if (requiresSoftReservationFallback(listing)) {
    // The price the checkout SHOWS has to be the price the server CHARGES. Since the payload
    // started carrying priceVariantName, api-util/lineItems.js prices the booking from the selected
    // variant — so shipping the listing's base price alongside it meant the visitor confirmed a
    // card against one number and the provider charged another.
    const priceVariants = listing.attributes.publicData?.priceVariants || [];
    const selectedVariant = priceVariantName
      ? priceVariants.find(pv => pv.name === priceVariantName)
      : null;
    const basePrice = listing.attributes.price;
    const listingPrice =
      selectedVariant?.priceInSubunits != null
        ? { amount: selectedVariant.priceInSubunits, currency: basePrice?.currency }
        : basePrice;
    // The API dates and the dates a human should read are not the same thing for day/week/month
    // units, whose end is exclusive. Both travel: the first pair is what gets initiated, the second
    // is what the checkout renders — otherwise the login dialog says "14. Sep" and the checkout it
    // resumes into says "14.9. – 15.9." for the same single-day pass.
    const summaryDates = bookingSummaryDates(bookingDatesMaybe, listing);
    const softBookingData = {
      listingId: listing.id.uuid,
      bookingStart: initialValues.orderData?.bookingDates?.bookingStart,
      bookingEnd: initialValues.orderData?.bookingDates?.bookingEnd,
      displayEnd: summaryDates.bookingEnd,
      isDateOnly: summaryDates.isDateOnly,
      timeZone: summaryDates.timeZone,
      listingTitle: listing.attributes.title,
      // The server prices and initiates with this (`seats: seats || 1` in
      // server/api/soft-booking/initiate.js). Dropping it here is what made a two-seat selection
      // silently become a one-seat booking.
      seats: Number.isInteger(seats) ? seats : 1,
      // The purchase track prices exclusively off this (lineItems.js reads
      // orderData.stockReservationQuantity for line-item/item), and it is the ONLY thing a
      // purchase order form collects — there are no dates on that path at all. Leaving it out of
      // the payload is what made the whole track unreachable in the first place.
      quantity: Number.isInteger(quantity) ? quantity : null,
      // The checkout page picks its track off this, and the summary decides between a period and a
      // quantity with it. Without it a purchase payload looks like a booking payload that lost its
      // dates, and the page falls back to the "Diese Buchung ist nicht mehr verfügbar" screen —
      // measured on the test stage before this line existed.
      //
      // It carries the DECISION, not the raw unitType: the server derives the same thing from the
      // listing's process, so both ends stay anchored to one fact instead of two that can drift.
      isItemOrder: usesStockTrack(listing),
      // The variant IS the customer's selection on a fixed-duration listing: it drives both the
      // duration and the price. Dropping it here meant the server priced the booking from the
      // listing's base price instead of the CHF 89 "Ganztag" the customer actually chose.
      priceVariantName: priceVariantName || null,
      // Plain data, not the SDK Money instance. History state goes through structured clone, which
      // keeps the own properties and drops the prototype, while the session-storage copy is revived
      // by sdkTypes.reviver as a real Money — so the same field arrived with two different runtime
      // shapes depending on the route in, and anything reaching for formatMoney would throw on one
      // of them. The page only ever reads amount and currency.
      price: listingPrice ? { amount: listingPrice.amount, currency: listingPrice.currency } : null,
      // Purchase track. server/api-util/lineItems.js decides the shipping fee off
      // orderData.deliveryMethod and off nothing else
      // (getItemQuantityAndLineItems). Left out of this payload it arrives `undefined` at the
      // server, isShipping is false, and line-item/shipping-fee simply never appears: the customer
      // confirms a card against a total that is missing the shipping fee and the provider eats it,
      // while the SAME listing bought through the standard checkout is priced correctly.
      // Not triggerable on prod today (none of the 342 default-purchase listings has
      // shippingEnabled), which is precisely why it would have gone unnoticed until the first one
      // did.
      deliveryMethod: deliveryMethod || null,
      // Not the sessionStorage envelope's savedAt — that one is added by write() and stripped by
      // read(), so it never reaches this payload. This is the history-state copy's own age stamp,
      // and it is the only thing that can expire a `history.location.state` entry: unlike the
      // storage mirror, history state has no TTL at all, so the Back button could revive a
      // days-old checkout. Epoch milliseconds, because the two transports serialize differently
      // (structured clone keeps a Date, JSON does not) and a number survives both unchanged.
      savedAt: Date.now(),
    };
    // SoftBookingCheckoutPage renders from history state, which any redirect drops. Mirroring the
    // payload into session storage is what lets that page recover instead of showing "–" for the
    // dates and a submit button that cannot work.
    saveSoftBookingData(softBookingData);
    history.push(
      createResourceLocatorString('SoftBookingCheckoutPage', routes, {}, {}),
      softBookingData
    );
    return;
  }

  // Customize checkout page state with current listing and selected orderData
  const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

  callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

  // Clear previous Stripe errors from store if there is any
  onInitializeCardPaymentData();

  // Redirect to CheckoutPage
  history.push(
    createResourceLocatorString(
      'CheckoutPage',
      routes,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    )
  );
};

/**
 * Replay the booking a visitor put together before they had an account.
 *
 * handleSubmit stored the order panel's form values and opened the login dialog rather than
 * navigating away, so the listing page is still mounted (password sign-in) or freshly mounted at
 * the same URL (social sign-in and the sign-up detour, both of which return here via `from`).
 * Either way, the moment the session exists the stored values go back through handleSubmit and the
 * visitor lands in checkout with the dates, seats and price variant they had already chosen.
 *
 * Must be called before this component's early returns — it is a hook, and the loading and error
 * branches sit further down.
 *
 * @param {Object} parameters the same object handleSubmit takes, plus:
 * @param {boolean} parameters.isAuthenticated
 * @param {Object} parameters.currentUser
 * @param {Object} parameters.listing - the listing currently on screen
 */
export const usePendingBookingResume = parameters => {
  const { isAuthenticated, currentUser, listing } = parameters;

  // A resume navigates. Doing it twice — React 18 double-invokes effects in development, and the
  // listing entity is replaced on every fetch — would push checkout onto the history stack twice.
  const resumedRef = useRef(false);

  const listingId = listing?.id?.uuid;
  const currentUserId = currentUser?.id?.uuid;
  const authorId = listing?.author?.id?.uuid;
  // requiresSoftReservationFallback decides between the Stripe checkout and the manual-charge one
  // by reading the author's `profile.publicData.stripeConnected`, and it reads a missing value as
  // "not connected" — so resuming before the author's publicData is in the store would route a
  // Stripe-verified provider's booking into soft-booking.
  //
  // ⚠️ Die frühere Fassung prüfte `!!...profile?.publicData` und war damit WIRKUNGSLOS: sanitizeUser
  // destrukturiert mit `publicData = {}`, und weil `{}` truthy ist, trägt JEDE bereinigte
  // Nutzer-Entität den Schlüssel — auch die sparsame. Der Guard war also immer true, genau bei der
  // sparsamen Entität, gegen die er schützen sollte. Deshalb jetzt auf INHALT prüfen.
  //
  // Die eigentliche Absicherung sind die `fields.user`-Fieldsets, die `profile.publicData`
  // inzwischen überall mit anfordern; dieser Guard ist die zweite Reihe.
  const authorPublicData = listing?.author?.attributes?.profile?.publicData;
  const hasFullAuthor = !!authorPublicData && Object.keys(authorPublicData).length > 0;
  const isListingReady =
    !!listingId &&
    !!authorId &&
    hasFullAuthor &&
    !!listing?.attributes?.publicData?.transactionProcessAlias &&
    listing?.attributes?.state !== LISTING_STATE_CLOSED;
  const isOwnListing = !!currentUserId && currentUserId === authorId;
  // A brand-new account can be in pending-approval (or banned) state. handleContactUser refuses to
  // open its dialog for those users; auto-navigating them into a checkout — on the soft-booking
  // path, all the way to entering card details before the server refuses — would be worse.
  //
  // Deliberately NOT also gating on hasPermissionToInitiateTransactions: that helper returns false
  // whenever effectivePermissionSet is missing from the currentUser fetch, which would silently
  // strand fully authorised users. handleSubmit skips it for the same documented reason.
  const canTransact = !currentUser || isUserAuthorized(currentUser);
  const canResume =
    isAuthenticated && !!currentUserId && canTransact && isListingReady && !isOwnListing;

  useEffect(() => {
    if (resumedRef.current || !canResume) {
      return;
    }
    const pending = readPendingBooking();
    // Only ever resume on the listing the visitor was actually booking. Signing in from the topbar
    // somewhere else must not drag them into a checkout they did not ask for.
    if (!pending || pending.listingId !== listingId) {
      return;
    }
    resumedRef.current = true;

    try {
      handleSubmit({ ...parameters, isAuthenticated: true })(pending.values);
    } catch (e) {
      // Not worth taking the listing page down for.
      log.error(e, 'pending-booking-resume-failed', { listingId });
    } finally {
      // Consumed either way. resumedRef already blocks a second attempt on this mount, so leaving
      // a record that threw in place would only mean the next visit to this listing inside the
      // window auto-navigates — or throws again — with no click of the visitor's.
      clearPendingBooking();
    }
  }, [canResume, listingId]); // eslint-disable-line
};

/**
 * Create fallback views for the ListingPage: LoadingPage and ErrorPage.
 * The PlainPage is just a helper for them.
 */
const PlainPage = props => {
  const { title, topbar, scrollingDisabled, children } = props;
  // Neither fallback is a page anyone should find in a search engine: the listing it stands in
  // for either exists (and has its own indexable render) or does not.
  return (
    <Page title={title} scrollingDisabled={scrollingDisabled} noIndex>
      <LayoutSingleColumn topbar={topbar} footer={<FooterContainer />}>
        {children}
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * Tell the server that this response is a temporary failure, not content (contract D2 of the SEO
 * plan): server/index.js answers HTTP 503 + Retry-After instead of 200. Before, a Sharetribe
 * 5xx/429/timeout during SSR reached Googlebot as a 200 "Eintrag wird geladen…" page — a soft 404
 * that also replaced the real listing in the index.
 *
 * `staticContext` only exists in the server render (StaticRouter); on the client it is absent or
 * a throw-away object, so writing to it changes nothing in the markup and cannot break hydration.
 *
 * @param {Object} [staticContext] StaticRouter context
 */
const markTemporarilyUnavailable = staticContext => {
  if (staticContext) {
    staticContext.unavailable = true;
  }
};

/**
 * Fallback when the listing could not be loaded (or is malformed).
 *
 * A load error is temporary → 503 (see markTemporarilyUnavailable). A listing that loaded but
 * lacks listingType/transactionProcessAlias/unitType (`invalidListing`) is a permanent data
 * defect; answering 503 for it forever would make Google slow down crawling the whole site, so it
 * only gets noindex.
 *
 * @param {Object} props
 * @param {ReactNode} props.topbar
 * @param {boolean} props.scrollingDisabled
 * @param {boolean} [props.invalidListing]
 * @param {Object} props.intl
 * @param {Object} [props.staticContext] StaticRouter context (server only)
 * @returns {JSX.Element}
 */
export const ErrorPage = props => {
  const { topbar, scrollingDisabled, invalidListing, intl, staticContext } = props;
  if (!invalidListing) {
    markTemporarilyUnavailable(staticContext);
  }
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.loadErrorTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.errorText}>
        {invalidListing ? (
          <FormattedMessage id="ListingPage.invalidDataMessage" />
        ) : (
          <FormattedMessage id="ListingPage.loadErrorMessage" />
        )}
      </p>
    </PlainPage>
  );
};

/**
 * Fallback while the listing is loading. In the browser that is a normal in-app navigation. In the
 * server render it means the data never arrived (loadData failed or timed out) → 503, never a 200
 * page that says "loading".
 *
 * @param {Object} props
 * @param {ReactNode} props.topbar
 * @param {boolean} props.scrollingDisabled
 * @param {Object} props.intl
 * @param {Object} [props.staticContext] StaticRouter context (server only)
 * @returns {JSX.Element}
 */
export const LoadingPage = props => {
  const { topbar, scrollingDisabled, intl, staticContext } = props;
  markTemporarilyUnavailable(staticContext);
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.loadingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.loadingText}>
        <FormattedMessage id="ListingPage.loadingMessage" />
      </p>
    </PlainPage>
  );
};
